/**
 * Subdomain Detection Utility
 *
 * Detects whether the current URL represents:
 * - A user subdomain (e.g., username.tweek.io) → show public speaker page
 * - An app subdomain (e.g., app.tweek.io) → show auth-protected app
 *
 * Supports development mode with username query parameter
 */

/**
 * Information about the current subdomain and routing context
 */
export interface SubdomainInfo {
  /** Whether the current URL is a user-specific subdomain (e.g., username.tweek.io) */
  isUserSubdomain: boolean;
  /** The username extracted from the subdomain, or null if not a user subdomain */
  username: string | null;
  /** Whether the current URL is an app subdomain (e.g., app.tweek.io, www.tweek.io) */
  isAppSubdomain: boolean;
  /** The full domain/hostname of the current URL (e.g., "localhost", "username.tweek.io", "app.tweek.io") */
  domain: string;
}

/**
 * Valid domains that support subdomain routing
 */
const VALID_DOMAINS = [
  "tweek",
  "stagepanda",
  "thepresenta",
  "teamdocs",
  "demo",
  "stage",
];

/**
 * App subdomains that should trigger normal authentication flow
 */
const APP_SUBDOMAINS = ["app", "www", "development"];

/**
 * Gets subdomain information from the current URL
 *
 * @returns SubdomainInfo object with routing details
 */
export const getSubdomainInfo = (): SubdomainInfo => {
  const hostname = window.location.hostname;

  // Development mode: localhost
  if (hostname.includes("localhost") || hostname === "127.0.0.1") {
    const urlParams = new URLSearchParams(window.location.search);
    const username = urlParams.get("username");

    if (username) {
      return {
        isUserSubdomain: true,
        username: username,
        isAppSubdomain: false,
        domain: hostname,
      };
    }

    // No username param, treat as app subdomain (normal auth flow)
    return {
      isUserSubdomain: false,
      username: null,
      isAppSubdomain: true,
      domain: hostname,
    };
  }

  // Production mode: subdomain detection
  const hostnameParts = hostname.split(".");

  // Need at least subdomain.domain.tld (3 parts)
  if (hostnameParts.length < 3) {
    return {
      isUserSubdomain: false,
      username: null,
      isAppSubdomain: true,
      domain: hostname,
    };
  }

  const subdomain = hostnameParts[0];
  const domain = hostnameParts[1];

  // Check if domain is valid
  const isValidDomain = VALID_DOMAINS.includes(domain);

  if (!isValidDomain) {
    return {
      isUserSubdomain: false,
      username: null,
      isAppSubdomain: true,
      domain: hostname,
    };
  }

  // Check if subdomain is an app subdomain
  const isAppSubdomain =
    APP_SUBDOMAINS.includes(subdomain) || subdomain === "demo";

  if (isAppSubdomain) {
    return {
      isUserSubdomain: false,
      username: null,
      isAppSubdomain: true,
      domain: hostname,
    };
  }

  // It's a user subdomain
  console.log({
    isUserSubdomain: true,
    username: subdomain,
    isAppSubdomain: false,
    domain: hostname,
  }, "Value for this hereee")
  return {
    isUserSubdomain: true,
    username: subdomain,
    isAppSubdomain: false,
    domain: hostname,
  };
};

/**
 * Helper function to check if current route is for a public speaker page
 *
 * @returns boolean indicating if this is a public speaker page context
 */
export const isPublicSpeakerPage = (): boolean => {
  const subdomainInfo = getSubdomainInfo();
  const pathname = window.location.pathname;

  return subdomainInfo.isUserSubdomain && pathname === "/";
};

/**
 * Constructs a URL pointing to the app subdomain
 *
 * Useful when on a user subdomain (e.g., john.tweek.io) and need to
 * link back to the main app (e.g., app.tweek.io/overview)
 *
 * @param path - The path to append to the app subdomain URL (e.g., "/overview")
 * @returns Full URL with app subdomain (e.g., "https://app.tweek.io/overview")
 *
 * @example
 * // On john.tweek.io
 * getAppUrl("/overview") // Returns "https://app.tweek.io/overview"
 *
 * // On localhost with port
 * getAppUrl("/dashboard") // Returns "http://localhost:3000/dashboard"
 */
export const getAppUrl = (path: string): string => {
  const { protocol, hostname, port } = window.location;

  // For localhost, just return the path as-is (no subdomain switching needed)
  if (hostname.includes("localhost") || hostname === "127.0.0.1") {
    const portSuffix = port ? `:${port}` : "";
    return `${protocol}//${hostname}${portSuffix}${path}`;
  }

  const hostParts = hostname.split(".");

  // Replace the first subdomain (username) with "app"
  // e.g., john.tweek.io -> app.tweek.io
  if (hostParts.length >= 2) {
    hostParts[0] = "app";
  }

  const newHost = hostParts.join(".");
  const portSuffix = port ? `:${port}` : "";

  return `${protocol}//${newHost}${portSuffix}${path}`;
};
